/**
 * Şablonların ortak kullandığı araçlar.
 *
 * Buradaki iki fonksiyon kritik: `carveMaze` çözülebilir labirent üretir,
 * `solveMaze` onu doğrular. Üretici bir sayfa bastıktan sonra çözümü
 * olduğunu KANITLAMADAN teslim etmeyiz — çocuğun önüne çözümsüz bir
 * labirent koymak, bir egzersiz programının yapabileceği en kötü şey.
 */

import { Rng } from '../prng'
import { sekilMi } from '../render/shapes'
import type { ShapeName } from '../render/shapes'
import type { CellNode, GradeResult, ItemResult } from './types'

/** Çoklu maddeli cevabı puanlar: sıra ve değer karşılaştırması */
export function gradeList(
  expected: readonly unknown[],
  answer: unknown,
  /** Madde kimlikleri (satır id'leri); verilmezse sıra numarası kullanılır */
  ids?: readonly string[],
): GradeResult {
  const got = Array.isArray(answer) ? answer : [answer]
  // Karşılaştırma Türkçe işaretlerden bağımsız: ölçtüğümüz şey imla değil
  const items: ItemResult[] = expected.map((e, i) => {
    const ham = got[i]
    const verilen = ham === undefined || ham === null || String(ham) === '' ? null : String(ham)
    return {
      id: ids?.[i] ?? String(i + 1),
      beklenen: String(e),
      verilen,
      dogru: verilen !== null && foldTr(verilen) === foldTr(String(e)),
    }
  })
  const perItem = items.map((x) => x.dogru)
  const correct = perItem.filter(Boolean).length
  return {
    correct,
    total: expected.length,
    accuracy: expected.length === 0 ? 0 : correct / expected.length,
    perItem,
    items,
  }
}

/** Sırası önemsiz küme cevabı: hangi hücreler işaretlendi */
export function gradeSet(expected: readonly string[], answer: unknown): GradeResult {
  const got = new Set((Array.isArray(answer) ? answer : []).map(String))
  const want = new Set(expected.map(String))
  let hits = 0
  for (const w of want) if (got.has(w)) hits++
  // Fazladan işaretlenen her hücre bir hata sayılır: rastgele hepsini
  // işaretleyip tam puan almak mümkün olmamalı
  let falseAlarms = 0
  for (const g of got) if (!want.has(g)) falseAlarms++
  const correct = Math.max(0, hits - falseAlarms)

  /**
   * Detay iki parçalı: beklenen her hücre (bulundu mu) ve fazladan
   * işaretlenen her hücre. "6 doğrudan 4'ünü buldu" ile "6'sını da buldu
   * ama 5 fazla işaret koydu" aynı puanı verebiliyor; ikisi bambaşka iki
   * davranış ve uygulayıcının ayırt edebilmesi gerekiyor.
   */
  const items: ItemResult[] = [
    ...[...want].map((w) => ({
      id: w,
      beklenen: 'işaretlenmeli',
      verilen: got.has(w) ? 'işaretlendi' : null,
      dogru: got.has(w),
    })),
    ...[...got]
      .filter((g) => !want.has(g))
      .map((g) => ({
        id: g,
        beklenen: 'işaretlenmemeli',
        verilen: 'işaretlendi',
        dogru: false,
        fazladan: true,
      })),
  ]

  return {
    correct,
    total: want.size,
    accuracy: want.size === 0 ? 0 : correct / want.size,
    perItem: [...want].map((w) => got.has(w)),
    items,
  }
}

/**
 * Tek cevaplı egzersizler (labirent, matris, ayna) için.
 *
 * Tek maddelik de olsa detayın aynı biçimde çıkması gerekiyor; yoksa
 * panel her egzersiz için ayrı bir okuma kuralı öğrenmek zorunda kalır.
 */
export function gradeSingle(
  beklenen: unknown,
  verilen: unknown,
  id = '1',
): GradeResult {
  const v =
    verilen === undefined || verilen === null || String(verilen) === ''
      ? null
      : String(verilen)
  const dogru = v !== null && foldTr(v) === foldTr(String(beklenen))
  return {
    correct: dogru ? 1 : 0,
    total: 1,
    accuracy: dogru ? 1 : 0,
    perItem: [dogru],
    items: [{ id, beklenen: String(beklenen), verilen: v, dogru }],
  }
}

/**
 * Harf havuzu. İlk sekizi düz harf: yol takibi gibi etiket isteyen
 * egzersizler baştan alıyor. Sonrakiler Türkçeye özgü harfler — kelime
 * bulmaca dolgusunda metnin Türkçe görünmesi için gerekli.
 */
export const LETTERS = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H',
  'K', 'L', 'M', 'N', 'O', 'P', 'R', 'S', 'T', 'U', 'V', 'Y', 'Z',
  'Ç', 'Ğ', 'İ', 'Ö', 'Ş', 'Ü',
] as const

/** Türkçe büyük harf: i → İ, ı → I. Düz toUpperCase bunu yanlış yapar. */
export function trUpper(s: string): string {
  return s.toLocaleUpperCase('tr-TR')
}

/**
 * Karşılaştırma için sadeleştirme.
 *
 * Çocuk "araç" yazdığında beklenen "arac" ise sistem yanlış demez;
 * ölçtüğümüz şey imla değil. Büyük-küçük harf ve Türkçe işaretler
 * karşılaştırmada yok sayılır, ama METİNDE korunur.
 */
export function foldTr(s: string): string {
  return s
    .toLocaleLowerCase('tr-TR')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Uyaran kümeleri — artık Unicode karakter değil, şekil ADI.
 *
 * Geometrileri `render/shapes.ts`'te. Buradaki dizeler hem çizime
 * (hangi şekil) hem puanlamaya (cevabın kimliği) gidiyor; ikisinin
 * aynı dize olması, "çocuk neyi seçti" ile "ekranda ne duruyordu"
 * arasında çeviri katmanı bırakmıyor.
 */
export const SHAPES_SIMPLE: ShapeName[] = ['daire', 'kare', 'ucgen', 'yildiz', 'elmas', 'kalp']

/**
 * Karıştırıcı küme: altısı da daire, farkı yalnızca dolu bölge.
 *
 * Eski `◐ ◑ ◒ ◓ ◔ ◕` kümesiyle aynı işi görüyor ama ayrımın keskinliği
 * fontun değil bizim elimizde.
 */
export const SHAPES_CONFUSABLE: ShapeName[] = [
  'yarim_sol',
  'yarim_sag',
  'yarim_ust',
  'yarim_alt',
  'ceyrek',
  'ucceyrek',
]

/** İçi boş karşılıklar; `SHAPES_SIMPLE` ile aynı sırada */
export const SHAPES_HOLLOW: ShapeName[] = [
  'daire_bos',
  'kare_bos',
  'ucgen_bos',
  'yildiz_bos',
  'elmas_bos',
  'kalp_bos',
]

export const ARROWS: ShapeName[] = [
  'ok_yukari',
  'ok_sag',
  'ok_asagi',
  'ok_sol',
  'ok_sol_yukari',
  'ok_sag_yukari',
  'ok_sag_asagi',
  'ok_sol_asagi',
]

export const WORDS_TR = [
  'kalem', 'kitap', 'masa', 'pencere', 'bahçe', 'deniz', 'bulut', 'orman',
  'köprü', 'yıldız', 'çiçek', 'sandalye', 'bardak', 'anahtar', 'tren',
  'balık', 'ayna', 'defter', 'sepet', 'yaprak', 'kardeş', 'okul',
] as const

export const CATEGORIES_TR: Record<string, string[]> = {
  meyve: ['elma', 'armut', 'kiraz', 'erik', 'üzüm', 'incir'],
  hayvan: ['kedi', 'köpek', 'tavşan', 'kaplumbağa', 'kartal', 'balina'],
  araç: ['otobüs', 'tren', 'bisiklet', 'vapur', 'uçak', 'kamyon'],
  eşya: ['kalem', 'defter', 'bardak', 'tabak', 'sandalye', 'anahtar'],
}

/**
 * Kimlik dizesinden hücre/uyaran üretir.
 *
 * Yalnızca havuzun KARIŞIK olduğu yerde gerekli: eşleştirme belleği
 * şekillerle harfleri aynı desteye koyuyor, dizi hatırlama düzeye göre
 * şekil ya da rakam gösteriyor. Oralarda "bu dize şekil mi harf mi"
 * sorusu üretim anında cevaplanıyor ve çizim tarafına TÜRLE söyleniyor;
 * çizim tarafı aramak zorunda kalmıyor.
 *
 * Havuzu sabit olan şablonlar bunu kullanmaz, doğrudan `{ shape }` ya da
 * `{ glyph }` yazar.
 */
export function isaret(ad: string): { shape?: ShapeName; glyph?: string } {
  return sekilMi(ad) ? { shape: ad } : { glyph: ad }
}

/** Boş hücre dizisi */
export function emptyCells(n: number): CellNode[] {
  return Array.from({ length: n }, () => ({}))
}

/* ------------------------------------------------------------------ *
 * Labirent
 * ------------------------------------------------------------------ */

export interface Maze {
  cols: number
  rows: number
  /** Her hücre için [üst, sağ, alt, sol] duvar durumu */
  walls: [boolean, boolean, boolean, boolean][]
  start: number
  end: number
}

/**
 * Derinlik öncelikli kazıma. Üretilen labirent bir AĞAÇ olduğu için
 * her hücre çiftinin arasında tam olarak bir yol vardır — çözülebilirlik
 * yapı gereği garanti. Yine de solveMaze ile doğruluyoruz, çünkü
 * "garanti olmalı" ile "garanti" arasındaki fark bir hata ayıklama
 * gecesine mal olur.
 */
export function carveMaze(rng: Rng, cols: number, rows: number, extraOpenings = 0): Maze {
  const n = cols * rows
  const walls: [boolean, boolean, boolean, boolean][] = Array.from({ length: n }, () => [
    true, true, true, true,
  ])
  const visited = new Array<boolean>(n).fill(false)
  const stack: number[] = [0]
  visited[0] = true

  const neighbours = (i: number): { cell: number; dir: number }[] => {
    const r = Math.floor(i / cols)
    const c = i % cols
    const out: { cell: number; dir: number }[] = []
    if (r > 0) out.push({ cell: i - cols, dir: 0 })
    if (c < cols - 1) out.push({ cell: i + 1, dir: 1 })
    if (r < rows - 1) out.push({ cell: i + cols, dir: 2 })
    if (c > 0) out.push({ cell: i - 1, dir: 3 })
    return out
  }

  while (stack.length > 0) {
    const cur = stack[stack.length - 1]!
    const options = neighbours(cur).filter((x) => !visited[x.cell])
    if (options.length === 0) {
      stack.pop()
      continue
    }
    const choice = options[rng.int(0, options.length - 1)]!
    walls[cur]![choice.dir] = false
    walls[choice.cell]![(choice.dir + 2) % 4] = false
    visited[choice.cell] = true
    stack.push(choice.cell)
  }

  // Ek açıklıklar: labirenti daha kolay ve döngülü yapar (düşük seviyeler)
  for (let k = 0; k < extraOpenings; k++) {
    const i = rng.int(0, n - 1)
    const opts = neighbours(i)
    if (opts.length === 0) continue
    const o = opts[rng.int(0, opts.length - 1)]!
    walls[i]![o.dir] = false
    walls[o.cell]![(o.dir + 2) % 4] = false
  }

  return { cols, rows, walls, start: 0, end: n - 1 }
}

/** Genişlik öncelikli arama: yol varsa hücre dizisini döndürür */
export function solveMaze(m: Maze): number[] | null {
  const n = m.cols * m.rows
  const prev = new Array<number>(n).fill(-1)
  const seen = new Array<boolean>(n).fill(false)
  const queue = [m.start]
  seen[m.start] = true

  while (queue.length > 0) {
    const cur = queue.shift()!
    if (cur === m.end) {
      const path: number[] = []
      for (let at = cur; at !== -1; at = prev[at]!) path.push(at)
      return path.reverse()
    }
    const r = Math.floor(cur / m.cols)
    const c = cur % m.cols
    const w = m.walls[cur]!
    const moves: [boolean, number][] = [
      [!w[0] && r > 0, cur - m.cols],
      [!w[1] && c < m.cols - 1, cur + 1],
      [!w[2] && r < m.rows - 1, cur + m.cols],
      [!w[3] && c > 0, cur - 1],
    ]
    for (const [ok, next] of moves) {
      if (ok && !seen[next]) {
        seen[next] = true
        prev[next] = cur
        queue.push(next)
      }
    }
  }
  return null
}

/* ------------------------------------------------------------------ *
 * Mini sudoku (4×4 ve 6×6)
 * ------------------------------------------------------------------ */

/** Tam dolu geçerli ızgara üretir */
export function fullSudoku(rng: Rng, size: 4 | 6): number[] {
  const boxW = size === 4 ? 2 : 3
  const boxH = 2
  const grid = new Array<number>(size * size).fill(0)

  const ok = (i: number, v: number): boolean => {
    const r = Math.floor(i / size)
    const c = i % size
    for (let k = 0; k < size; k++) {
      if (grid[r * size + k] === v) return false
      if (grid[k * size + c] === v) return false
    }
    const br = Math.floor(r / boxH) * boxH
    const bc = Math.floor(c / boxW) * boxW
    for (let rr = 0; rr < boxH; rr++) {
      for (let cc = 0; cc < boxW; cc++) {
        if (grid[(br + rr) * size + (bc + cc)] === v) return false
      }
    }
    return true
  }

  const fill = (i: number): boolean => {
    if (i === size * size) return true
    const candidates = rng.shuffle(Array.from({ length: size }, (_, k) => k + 1))
    for (const v of candidates) {
      if (ok(i, v)) {
        grid[i] = v
        if (fill(i + 1)) return true
        grid[i] = 0
      }
    }
    return false
  }

  fill(0)
  return grid
}

/** Çözüm sayısını sayar (2'de durur: tekliği kanıtlamak yeter) */
export function countSudokuSolutions(puzzle: number[], size: 4 | 6, limit = 2): number {
  const boxW = size === 4 ? 2 : 3
  const boxH = 2
  const g = puzzle.slice()
  let count = 0

  const ok = (i: number, v: number): boolean => {
    const r = Math.floor(i / size)
    const c = i % size
    for (let k = 0; k < size; k++) {
      if (k !== c && g[r * size + k] === v) return false
      if (k !== r && g[k * size + c] === v) return false
    }
    const br = Math.floor(r / boxH) * boxH
    const bc = Math.floor(c / boxW) * boxW
    for (let rr = 0; rr < boxH; rr++) {
      for (let cc = 0; cc < boxW; cc++) {
        const idx = (br + rr) * size + (bc + cc)
        if (idx !== i && g[idx] === v) return false
      }
    }
    return true
  }

  const solve = (i: number): void => {
    if (count >= limit) return
    if (i === size * size) {
      count++
      return
    }
    if (g[i] !== 0) {
      solve(i + 1)
      return
    }
    for (let v = 1; v <= size; v++) {
      if (ok(i, v)) {
        g[i] = v
        solve(i + 1)
        g[i] = 0
        if (count >= limit) return
      }
    }
  }

  solve(0)
  return count
}

/**
 * İpucu silerek bulmaca üretir ve TEK ÇÖZÜMLÜ kalmasını garanti eder.
 * Tek çözümü olmayan bir bulmaca, çocuğun doğru cevabına yanlış demek
 * demektir; bu üretici bunu yapamaz.
 */
export function makeSudoku(rng: Rng, size: 4 | 6, cluesToRemove: number): { puzzle: number[]; solution: number[] } {
  const solution = fullSudoku(rng, size)
  const puzzle = solution.slice()
  const order = rng.shuffle(Array.from({ length: size * size }, (_, i) => i))
  let removed = 0

  for (const i of order) {
    if (removed >= cluesToRemove) break
    const backup = puzzle[i]!
    puzzle[i] = 0
    if (countSudokuSolutions(puzzle, size) !== 1) {
      puzzle[i] = backup
    } else {
      removed++
    }
  }
  return { puzzle, solution }
}

/* ------------------------------------------------------------------ */

/** Çakışmayan rastgele konumlar (serbest yerleşim için) */
export function spreadPoints(rng: Rng, count: number, minDist = 0.1): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = []
  let guard = 0
  while (pts.length < count && guard++ < count * 200) {
    const p = { x: 0.05 + rng.float() * 0.9, y: 0.05 + rng.float() * 0.9 }
    if (pts.every((q) => Math.hypot(q.x - p.x, q.y - p.y) >= minDist)) pts.push(p)
  }
  // Sığmadıysa ızgaraya düş: sayfa boş kalmasın
  while (pts.length < count) {
    const i = pts.length
    const side = Math.ceil(Math.sqrt(count))
    pts.push({ x: 0.08 + (0.84 * (i % side)) / side, y: 0.08 + (0.84 * Math.floor(i / side)) / side })
  }
  return pts
}
